// Dependencies
import { FC, useState } from "react";
import { useQuery } from "react-query";
import { useSnackbar } from "notistack";

// Components
import { BarLoading } from "~/components/Common/Loading";
import { Choose, When, If } from "jsx-control-statements";
import WalletAdd from "~/containers/Wallets/WalletAdd";
import WalletItem from "~/components/Wallets/WalletItem";
import WalletChooseType from "~/components/Wallets/WalletChooseType";

// Icons
import AddIcon from "@mui/icons-material/Add";

// Types
import { QUERY_KEY } from "~/enums";
import { IWalletType, TWalletData, TQueryError } from "~/types";

// api
import { getWalletList } from "~/services/wallet";

// Styles
import {
  StyledModal,
  StyledWalletList,
  StyledListWrapper,
} from "./WalletList.styled";
import { StyledInfoLabel } from "~/components/Layout/LayoutInfo";
import { StyledAddBtn, StyledNotFound } from "~/components/Layout";

interface WalletListProps {
  onShowDetail: Function;
}

const WalletList: FC<WalletListProps> = ({ onShowDetail }) => {
  // States
  const [openChooseType, setOpenChooseType] = useState<boolean>(false);
  const [openAdd, setOpenAdd] = useState<boolean>(false);
  const [addTypeWallet, setAddTypeWallet] = useState<keyof IWalletType>("Goal");

  const { enqueueSnackbar } = useSnackbar();

  const walletListQuery = useQuery({
    queryKey: QUERY_KEY.WALLET.WALLET_LIST,
    queryFn: getWalletList,
    onError(error: TQueryError) {
      enqueueSnackbar(error.message, { variant: "error" });
    },
  });

  // Methods
  const handleOpenAddModal = () => {
    setOpenAdd(true);
  };

  const handleCloseAddModal = () => {
    setOpenAdd(false);
    setOpenChooseType(false);
  };

  const handleOpenChooseTypeModal = () => {
    setOpenChooseType(true);
  };

  const handleCloseChooseTypeModal = () => {
    setOpenChooseType(false);
  };

  const handleShowDetail = (walletId: string) => {
    onShowDetail(walletId);
  };

  const handleChooseType = (walletType: keyof IWalletType) => {
    setAddTypeWallet(walletType);
    handleOpenAddModal();
  };

  const walletList: TWalletData[] = walletListQuery.data || [];

  return (
    <StyledListWrapper>
      <StyledInfoLabel>Wallet List</StyledInfoLabel>
      <Choose>
        <When condition={walletListQuery.isLoading}>
          <BarLoading />
        </When>
        <When condition={walletListQuery.isSuccess}>
          <If condition={walletList.length}>
            <StyledWalletList>
              {walletList.map((walletData) => (
                <WalletItem
                  type={walletData.type}
                  name={walletData.name}
                  currency={walletData.currencyId}
                  total={walletData.total}
                  onClick={() => handleShowDetail(walletData.id)}
                />
              ))}
            </StyledWalletList>
          </If>
          <If condition={!walletList.length}>
            <StyledNotFound>No wallet found!</StyledNotFound>
          </If>
        </When>
      </Choose>
      <StyledAddBtn
        onClick={handleOpenChooseTypeModal}
        variant={"outlined"}
        icon={<AddIcon />}
      >
        Create new wallet
      </StyledAddBtn>

      {/** For create wallet */}
      <StyledModal open={openChooseType} onClose={handleCloseChooseTypeModal}>
        <div>
          <WalletChooseType onChoose={handleChooseType} />
        </div>
      </StyledModal>
      <StyledModal open={openAdd}>
        <div>
          <WalletAdd type={addTypeWallet} onCancel={handleCloseAddModal} />
        </div>
      </StyledModal>
    </StyledListWrapper>
  );
};

export default WalletList;
